import { Edit, useForm, useSelect } from "@refinedev/antd";
import { Form, Input, Select, Tag, Space, Row, Col } from "antd";
import { IResourceComponentsProps } from "@refinedev/core";
import { EnvironmentOutlined, GlobalOutlined, LinkOutlined } from "@ant-design/icons";

import { IAddress, ICountry, IShipco, IShipcoGroup } from "interfaces";
import React from "react";
import { FormSection, FormFieldGrid } from "components/form";

export const AddressEdit: React.FC<IResourceComponentsProps> = () => {
  const {
    formProps,
    saveButtonProps,
    query,
    formLoading: isLoading,
  } = useForm<IAddress>({
    action: "edit",
  });

  function CountrySelectComponent() {
    const { selectProps } = useSelect<ICountry>({
      resource: "country",
      optionLabel: "name",
      optionValue: "id",
      filters: query?.data?.data?.country?.id
        ? [{ field: "id", operator: "eq", value: query?.data?.data?.country?.id }]
        : [],
      pagination: { mode: "server" }
    });
    return (
      <Form.Item
        label="Country"
        name={["country", "id"]}
        rules={[{ required: true }]}
        initialValue={query?.data?.data?.country?.name}
      >
        <Select {...selectProps} />
      </Form.Item>
    );
  }

  function ShipcoSelectComponent() {
    const { selectProps } = useSelect<IShipco>({
      resource: "shipco",
      optionLabel: "registrationName",
      optionValue: "id",
      filters: query?.data?.data?.shipco?.id
        ? [{ field: "id", operator: "eq", value: query?.data?.data?.shipco?.id }]
        : [],
      pagination: { mode: "server" }
    });
    return (
      <Form.Item
        label="Shipco"
        name={["shipco", "id"]}
        initialValue={query?.data?.data?.shipco?.registrationName}
      >
        <Select {...selectProps} />
      </Form.Item>
    );
  }

  function ShipcoGroupSelectComponent() {
    const { selectProps } = useSelect<IShipcoGroup>({
      resource: "shipco-group",
      optionLabel: "registrationName",
      optionValue: "id",
      filters: query?.data?.data?.shipcoGroup?.id
        ? [{ field: "id", operator: "eq", value: query?.data?.data?.shipcoGroup?.id }]
        : [],
      pagination: { mode: "server" }
    });
    return (
      <Form.Item
        label="Shipco Group"
        name={["shipcoGroup", "id"]}
        initialValue={query?.data?.data?.shipcoGroup?.registrationName}
      >
        <Select {...selectProps} />
      </Form.Item>
    );
  }

  return query?.isLoading ? (
    <React.Fragment>'Loading...'</React.Fragment>
  ) : (
    <Edit
      breadcrumb={false}
      saveButtonProps={saveButtonProps}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "edit address",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
    >
      <Form {...formProps} layout="vertical">
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <FormSection
            title="Address Details"
            subtitle="Street address information"
            icon={<EnvironmentOutlined />}
            variant="elevated"
          >
            <FormFieldGrid columns={1}>
              <Form.Item
                label="Address Line 1"
                name="addressLine1"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </FormFieldGrid>
            <FormFieldGrid columns={2}>
              <Form.Item label="Address Line 2" name="addressLine2">
                <Input />
              </Form.Item>
              <Form.Item label="Address Line 3" name="addressLine3">
                <Input />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>

          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <FormSection
                title="Location"
                subtitle="City, state and country"
                icon={<GlobalOutlined />}
              >
                <FormFieldGrid columns={2}>
                  <Form.Item
                    label="City"
                    name="city"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    label="State"
                    name="state"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item label="Postal Code" name="postalCode">
                    <Input />
                  </Form.Item>
                </FormFieldGrid>
                {query?.isSuccess ? <CountrySelectComponent /> : null}
              </FormSection>
            </Col>

            <Col xs={24} lg={12}>
              <FormSection
                title="Company Association"
                subtitle="Link to company"
                icon={<LinkOutlined />}
              >
                {query?.isSuccess ? (
                  <>
                    <ShipcoSelectComponent />
                    <ShipcoGroupSelectComponent />
                  </>
                ) : null}
              </FormSection>
            </Col>
          </Row>
        </Space>
      </Form>
    </Edit>
  );
};
